import {
  browserLocalPersistence,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { AuthResult } from 'src/pages/signin';
import { auth } from './firebaseConfig';

const googleProvider = new GoogleAuthProvider();

// Simple function to get the account type from category
export const getAccountTypeFromCategory = (category: string) => {
  if (!category) return null;

  const cleanCategory = category.trim().toLowerCase();

  // Staff categories
  if (cleanCategory === 'sp | office team' || cleanCategory === 'sp | office team ') {
    return 'Staff';
  }

  // Talent categories
  const talentCategories = [
    'sp | band / musician / vocalist',
    'sp | dj',
    'sp | mc',
    'sp | performer',
    'sp | sound production / producer',
    'sp | producer'
  ];

  if (talentCategories.includes(cleanCategory)) {
    return 'Talent';
  }

  // Client categories - any category containing 'client'
  if (cleanCategory.includes('client')) {
    return 'Client';
  }

  // Unknown category
  console.log(`Category "${category}" doesn't match any known type`);
  return null;
};

// Sign in with Google functionality
export const signInWithGoogle = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    const googleResult = await signInWithPopup(auth, googleProvider);
    const user = googleResult.user;

    return {
      success: true,
      user: user,
      error: null
    };
  } catch (error: any) {
    console.error('Google sign-in error:', error);
    return { success: false, user: null, error: error.message };
  }
};

// Sign out unauthorized user
export const signOutUnauthorizedUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    console.error('Error signing out:', error);
    return { success: false, error: error.message };
  }
};

// Sign in with email and password
export async function signInWithCredentials(email: string, password: string): Promise<AuthResult> {
  try {
    await setPersistence(auth, browserLocalPersistence);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    return {
      success: true,
      user: user,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      user: null,
      error: error.message || 'Failed to sign in with email/password'
    };
  }
}

// Sign out functionality
export const firebaseSignOut = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Auth state observer
export const onAuthStateChanged = (callback: (user: any) => void) => {
  return auth.onAuthStateChanged(callback);
};

// Send password reset email
export const sendPasswordReset = async (email: string): Promise<AuthResult> => {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      success: true,
      error: null
    };
  } catch (error: any) {
    // Provide more specific error messages
    let errorMessage = error.message || 'Failed to send password reset email';
    
    switch (error.code) {
      case 'auth/user-not-found':
        // For security reasons, we don't want to reveal if an email exists
        errorMessage = 'If an account with that email exists, we\'ve sent you a password reset link.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many requests. Please try again later.';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection and try again.';
        break;
      default:
        errorMessage = 'Failed to send password reset email. Please try again.';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};
